import { useMemo, useState } from 'react'
import TagManager from 'react-gtm-module'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, CircularProgress, CssBaseline, ThemeProvider } from '@mui/material'
import { useSnackbar } from 'notistack'
import queryString from 'query-string'

import { useCreateOrganisationMutation } from '@/app/services/user'
import { selectCurrentUser, setCurrentUser, setUserRole } from '@/app/slices/appSlice'
import { setOrganisation } from '@/app/slices/organisationSlice'
import { useIntercomMessenger, useToolBoxTreatments } from '@/common/hooks'
import { RightArrowIcon } from '@/common/icons'
import { theme as lightTheme } from '@/common/themes/LightTheme'
import { StrikerThemeOverride } from '@/common/themes/overrides/StrikerThemeOverride'
import { WhitelabelThemeOverride } from '@/common/themes/overrides/WhitelabelThemeOverride'
import { getCookieByName, getLanguageToUse, getLocaleToUse, initUserFlow, Paths, RESELLERS } from '@/common/utils'

import { AccountDetailsSection } from './components/AccountDetailsSection'
import { DefaultSettingsSection } from './components/DefaultSettingsSection'
import { OrganizationFormLayout } from './components/OrganizationFormLayout'
import { FORM_FIELDS } from './constants/formConstants'
import { useOrganizationForm } from './hooks/useOrganizationForm'
import { useVoucherValidation } from './hooks/useVoucherValidation'

/**
 * Helper function to get HubSpot UTK (User Token) from URL params or cookies
 */
const getHutkValue = (params) => {
    if (params.hutk) {
        return params.hutk
    }
    return getCookieByName('hubspotutk')
}

/**
 * Helper function to extract country name with special handling for USA
 */
const getCountryName = (country) => {
    return country.alpha3 === 'USA' ? country.alternate_names[0] : country.name
}

/**
 * UserWelcome - Organization onboarding and setup flow
 *
 * Features:
 * - Multi-tenant organization creation
 * - Xero SSO integration with auto-fill
 * - IP-based geolocation for currency assignment
 * - Voucher code validation with debouncing
 * - HubSpot and FirstPromoter tracking integration
 * - Whitelabel theme support (Libellula, Striker)
 */
const UserWelcome = () => {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const { isReady, showIntercom, showLibellulaWhitelabelling, showStrikerWhitelabelling, showVoucherCode } =
        useToolBoxTreatments()

    // Theme selection based on feature flags
    const theme = useMemo(
        () =>
            showLibellulaWhitelabelling
                ? WhitelabelThemeOverride(lightTheme)
                : showStrikerWhitelabelling
                  ? StrikerThemeOverride(lightTheme)
                  : lightTheme,
        [showLibellulaWhitelabelling, showStrikerWhitelabelling]
    )

    const currentUser = useSelector(selectCurrentUser)
    const [createOrganisation] = useCreateOrganisationMutation()
    const { bootIntercomMessenger } = useIntercomMessenger()

    const params = queryString.parse(location.search)
    const isResellerEnvSet = !!import.meta.env.VITE_RESELLER

    const [isLoading, setIsLoading] = useState(false)

    // Custom hooks for organization form management
    const { control, countryList, formState, getValues, handleSubmit, setValue, watch } =
        useOrganizationForm(currentUser)

    // Voucher code validation
    const voucherCode = watch(FORM_FIELDS.ORGANISATION_VOUCHER_CODE)
    const { debouncedCode: debouncedVoucherCode, isValid: isValidVoucherCode } = useVoucherValidation(voucherCode)

    /**
     * Handle organization creation form submission
     */
    const handleNextButtonClick = async (data) => {
        if (!formState.isValid) return

        setIsLoading(true)

        const hutkValue = getHutkValue(params)
        const updatedUser = {
            ...currentUser,
            firstName: data[FORM_FIELDS.CONTACT_FIRST_NAME],
            lastName: data[FORM_FIELDS.CONTACT_LAST_NAME],
        }

        // FirstPromoter tracking cookies
        const refId = getCookieByName('_fprom_ref')
        const tId = getCookieByName('_fprom_tid')

        const countryName = getCountryName(data[FORM_FIELDS.ORGANISATION_COUNTRY])

        try {
            const options = {
                name: data[FORM_FIELDS.ORGANISATION_NAME],
                language: data[FORM_FIELDS.ORGANISATION_LANGUAGE],
                locale: data[FORM_FIELDS.ORGANISATION_LOCALE],
                useImperialUnits: data[FORM_FIELDS.ORGANISATION_UNITS].toLowerCase() === 'imperial',
                currencyCode: data[FORM_FIELDS.ORGANISATION_CURRENCY_CODE],
                userFirstName: updatedUser.firstName,
                userLastName: updatedUser.lastName,
                country: countryName,
                isLegacyMaterials: false,
                promotionalCode: isValidVoucherCode ? data[FORM_FIELDS.ORGANISATION_VOUCHER_CODE] : undefined,
                cuttingTechnologyPower: data[FORM_FIELDS.ORGANISATION_CUTTING_TECH_POWER],
                hubSpotUtk: isResellerEnvSet ? null : hutkValue,
                firstPromoterReferralId: refId,
                firstPromoterVisitorTrackingId: tId,
            }

            const organisationResponse = await createOrganisation({ userId: updatedUser.userId, options }).unwrap()

            // Update Redux state
            dispatch(setUserRole('owner'))
            dispatch(setOrganisation(organisationResponse))
            dispatch(setCurrentUser(updatedUser))

            // Initialize analytics for new organization (matches useAppInitialisation pattern)
            // Boot Intercom with new organization data
            if (showIntercom) {
                bootIntercomMessenger(organisationResponse, { user: updatedUser })
            }

            // Initialize UserFlow with new organization data
            initUserFlow({
                user: updatedUser,
                organisation: organisationResponse,
                localeToUse: getLocaleToUse(organisationResponse),
                language: getLanguageToUse(updatedUser, organisationResponse),
                planMetadata: undefined, // New org has no subscription yet
                reseller: import.meta.env.VITE_RESELLER ? RESELLERS[import.meta.env.VITE_RESELLER].name : undefined,
            })

            // Google Tag Manager tracking (non-reseller only)
            if (!isResellerEnvSet) {
                try {
                    const userEmailDomain = currentUser.emailAddress?.split('@')?.[1]

                    TagManager.dataLayer({
                        dataLayer: {
                            event: 'organisation_created',
                            userEmailDomain,
                        },
                    })
                } catch {
                    // Silently fail GTM tracking
                }
            }

            enqueueSnackbar(t('Organisation created'), {
                variant: 'success',
            })

            setIsLoading(false)
            navigate(Paths.DASHBOARD_PATHNAME)
        } catch (error) {
            setIsLoading(false)
            enqueueSnackbar(t('An error occurred') + '. ' + t('Please try again') + '.', {
                variant: 'error',
            })
        }
    }

    // Render action button
    const actionButton = (
        <Button
            color="primary"
            disabled={!formState.isValid || isLoading || !isReady || !isValidVoucherCode}
            endIcon={isLoading || !isReady ? <CircularProgress size={18} /> : <RightArrowIcon />}
            variant="contained"
            onClick={handleSubmit(handleNextButtonClick)}
        >
            {isLoading ? t('Creating organisation') : t('Create Organisation')}
        </Button>
    )

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <OrganizationFormLayout
                actionButton={actionButton}
                leftColumn={
                    <AccountDetailsSection
                        control={control}
                        countryList={countryList}
                        theme={theme}
                    />
                }
                rightColumn={
                    <DefaultSettingsSection
                        control={control}
                        debouncedVoucherCode={debouncedVoucherCode}
                        getValues={getValues}
                        isResellerEnv={isResellerEnvSet}
                        isValidVoucherCode={isValidVoucherCode}
                        showVoucherCode={showVoucherCode}
                        theme={theme}
                        watch={watch}
                    />
                }
                theme={theme}
            />
        </ThemeProvider>
    )
}

export default UserWelcome
